/**
 * Thread Add-People — pure create-then-add sequencing (group-threading P2c).
 *
 * Adding a participant is two authorized server calls: (1) ensure the membership
 * doc exists (`create` — idempotent; an existing doc resolves `already_exists`,
 * still ok) so the actor is recorded as creator when the thread was single-user,
 * then (2) `add` the target. Extracted as a pure function over injected callers so
 * the ordering + short-circuit are unit-tested without React or the network.
 *
 * Fail-closed: if `create` does not succeed we do NOT attempt `add` (never add into
 * a thread whose membership doc we couldn't establish). The server remains the sole
 * authority — this only sequences the two calls and reports which step decided.
 */
import type { MembershipWriteOutcome } from '../../repositories/threadMembershipWriteClient';

export interface AddParticipantFlowDeps {
  /** Ensure the membership doc exists (P2a `createThreadMembership`). */
  ensureThread: (threadId: string) => Promise<MembershipWriteOutcome>;
  /** Add the target participant (P2a `addThreadParticipant`). */
  addParticipant: (threadId: string, targetId: string) => Promise<MembershipWriteOutcome>;
}

export interface AddParticipantFlowResult {
  ok: boolean;
  /** The server (or client sentinel) decision that determined the outcome. */
  decision: string;
  /** Which step decided: `create` when the ensure step failed, else `add`. */
  step: 'create' | 'add' | 'input';
  threadId: string;
  targetId: string;
}

/**
 * Run the create-then-add flow. Short-circuits on blank input and on a failed
 * ensure step (no `add` attempted). Returns the deciding step + decision.
 */
export async function runAddParticipant(
  deps: AddParticipantFlowDeps,
  threadIdRaw: string,
  targetIdRaw: string,
): Promise<AddParticipantFlowResult> {
  const threadId = (threadIdRaw ?? '').trim();
  const targetId = (targetIdRaw ?? '').trim();

  if (!threadId || !targetId) {
    return { ok: false, decision: 'client_invalid_input', step: 'input', threadId, targetId };
  }

  const created = await deps.ensureThread(threadId);
  if (!created.ok) {
    // Fail-closed: never add into a thread whose membership doc wasn't established.
    return { ok: false, decision: created.decision, step: 'create', threadId, targetId };
  }

  const added = await deps.addParticipant(threadId, targetId);
  return { ok: added.ok, decision: added.decision, step: 'add', threadId, targetId };
}

/**
 * Map a failed flow result to user-facing copy. Keyed on the real server decision
 * codes (`threadMembershipWrite`) plus the client sentinels; falls back to a
 * step-appropriate generic. Copy only — never authorizes.
 */
export function describeAddParticipantFailure(
  res: Pick<AddParticipantFlowResult, 'decision' | 'step'>,
): string {
  switch (res.decision) {
    case 'already_participant':
      return "They're already in this thread.";
    case 'not_admittable_fail_closed':
      // Reachable when someone was invited but hasn't accepted, or when their
      // membership was suspended. Naming the next step beats a flat refusal.
      return "They aren't an active member of this workspace yet — check Team & Roles (they may not have accepted their invitation).";
    case 'account_boundary_fail_closed':
      return 'That person belongs to a different workspace, so they can’t be added here.';
    case 'workspace_boundary_fail_closed':
      return 'This thread belongs to another workspace — switch to that workspace to change who is in it.';
    case 'actor_not_member_fail_closed':
    case 'invite_not_allowed_fail_closed':
      return "You don't have permission to add people here — the person who started this thread, or a workspace owner or admin, can.";
    case 'remove_not_allowed_fail_closed':
      return "You don't have permission to remove people here — the person who started this thread, or a workspace owner or admin, can.";
    case 'assistant_toggle_not_allowed_fail_closed':
      return "You don't have permission to change whether Prest0n is in this thread — the person who started it, or a workspace owner or admin, can.";
    case 'thread_archived_fail_closed':
      return 'This thread was archived for everyone, so it can’t change any more.';
    case 'thread_not_found_fail_closed':
      return 'This thread is no longer available.';
    case 'invalid_target_fail_closed':
    case 'client_invalid_input':
      return 'Pick someone to add.';
    case 'unauthenticated_fail_closed':
    case 'client_no_auth':
    case 'client_no_token':
      return 'Please sign in again.';
    case 'client_network_error':
      return "Couldn't reach the server — check your connection and try again.";
    case 'client_http_error':
    case 'internal_error_fail_closed':
      return "The server couldn't complete that — try again in a moment.";
    default:
      return res.step === 'create' ? "Couldn't start the group here." : "Couldn't add that person.";
  }
}

/**
 * Copy for a FAILED remove (2026-09-15). The server's decision codes for a
 * removal are the membership write's own — the two that are removal-specific
 * (`remove_not_allowed_fail_closed`, `cannot_remove_creator_fail_closed`) are
 * named here; every other code (boundary, archived, not found, auth, network)
 * means the same thing it means on an add and reuses that wording. The default
 * says "remove", never "add" — the wrong verb on a failure notice is how a
 * person concludes the control did something other than what they pressed.
 */
export function describeRemoveParticipantFailure(decision: string): string {
  switch (decision) {
    case 'cannot_remove_creator_fail_closed':
      return "The person who started this thread can't be removed from it.";
    case 'already_not_participant':
      return "They're no longer in this thread.";
    case 'invalid_target_fail_closed':
    case 'client_invalid_input':
      return 'Pick someone to remove.';
    case 'remove_not_allowed_fail_closed':
      return "You don't have permission to remove people here — the person who started this thread, or a workspace owner or admin, can.";
    default: {
      const shared = describeAddParticipantFailure({ decision, step: 'add' });
      return shared === "Couldn't add that person." ? "Couldn't remove that person." : shared;
    }
  }
}
